import { useCallback, useMemo, useState } from 'react';
import type { CompanyTaskAttachmentInputDto } from '@gokrew/models/tasks';

export interface StagedFile {
  localId: string;
  file: File;
  uploading: boolean;
  result?: CompanyTaskAttachmentInputDto;
  error?: boolean;
}

/**
 * Tracks a list of in-progress file uploads (staged -> uploading -> result/error).
 * The actual upload call is injected so each portal keeps its own upload hook/endpoint.
 */
export function useStagedAttachments(
  uploadFn: (file: File) => Promise<{ url: string }>
) {
  const [staged, setStaged] = useState<StagedFile[]>([]);

  const addFiles = useCallback(
    async (files: FileList) => {
      const newStaged: StagedFile[] = Array.from(files).map((file) => ({
        localId: `${Date.now()}-${file.name}`,
        file,
        uploading: true,
      }));
      setStaged((prev) => [...prev, ...newStaged]);

      for (const item of newStaged) {
        try {
          const uploaded = await uploadFn(item.file);
          const result: CompanyTaskAttachmentInputDto = {
            key: uploaded.url,
            fileName: item.file.name,
            mimeType: item.file.type,
            fileSize: item.file.size,
          };
          setStaged((prev) =>
            prev.map((f) =>
              f.localId === item.localId
                ? { ...f, uploading: false, result }
                : f
            )
          );
        } catch {
          setStaged((prev) =>
            prev.map((f) =>
              f.localId === item.localId
                ? { ...f, uploading: false, error: true }
                : f
            )
          );
        }
      }
    },
    [uploadFn]
  );

  const removeStaged = useCallback((localId: string) => {
    setStaged((prev) => prev.filter((f) => f.localId !== localId));
  }, []);

  const reset = useCallback(() => setStaged([]), []);

  const readyAttachments = useMemo(
    () => staged.filter((f) => f.result && !f.error).map((f) => f.result!),
    [staged]
  );

  const hasUploading = useMemo(
    () => staged.some((f) => f.uploading),
    [staged]
  );

  return { staged, addFiles, removeStaged, reset, readyAttachments, hasUploading };
}
